import { useEffect, useRef } from "react";
import { createLiveStore, useRpc, rpc } from "../lib/live-store";
import { useConnectionStore } from "./connection";
import type { PresenceMap } from "../types/data-table";

interface PresenceData {
  presence: PresenceMap;
}

interface PresenceExtra {
  entity: "asset" | "user" | "queue";
  setEntity: (entity: "asset" | "user" | "queue") => void;
}

export const usePresenceStore = createLiveStore<PresenceData, PresenceExtra>({
  topic: (s) => `presence.${s.entity}s`,
  onSnapshot: (data) => ({ presence: data as PresenceMap }),
  state: (set) => ({
    presence: {},
    entity: "asset",
    setEntity: (entity: "asset" | "user" | "queue") => set({ entity, presence: {} } as any),
  }),
});

export function useRegisterPresence(
  entity: "asset" | "user" | "queue",
  entityId: number | undefined,
  enabled: boolean
) {
  const connected = useConnectionStore((s) => s.status === "connected");
  const registeredRef = useRef<{ entity: string; entityId: number } | null>(null);

  useEffect(() => {
    if (!enabled || entityId === undefined || !connected) return;

    rpc("presence.register", { entity, entityId }).catch(() => {});
    registeredRef.current = { entity, entityId };

    return () => {
      if (registeredRef.current) {
        rpc("presence.unregister", {
          entity: registeredRef.current.entity,
          entityId: registeredRef.current.entityId,
        }).catch(() => {});
        registeredRef.current = null;
      }
    };
  }, [entity, entityId, enabled, connected]);
}
